// The image-model showcase (MS-IMG, alongside V4-MS's video showcase in showcase.ts): every picture model on the same
// style prompts, from GET /api/model-showcase/images (public). Pure helpers (parse, pick, strip order) plus a cached
// loader; the Models screen's Images tab, the image pickers (Settings › Models, the compare sheet) and the stage read
// through here. Nothing shows until at least one still is made.

export type StillRow = { id: string; n: number; name: string; watch: string; kind: "style" | "ref"; tags: string[] };
export type StillModel = {
  id: string;
  label: string;
  short: string;
  provider: string;
  /** what one picture costs a user with it: the api's stepCredits, the same number the frame charge uses */
  credits: number;
  /** PRICING-1: one picture drawn from reference pictures (GPT Image 25, FLUX.2 Pro 54); null when it takes none */
  refCredits: number | null;
  /** it takes reference pictures (character sheets, product photos) */
  refs: boolean;
  rateSource: "documented" | "unverified";
  isDefault: boolean;
  madeSeconds: number | null;
};
export type StillCell = { rowId: string; modelId: string; imageUrl: string; thumbUrl: string; seconds: number | null; madeAt: string | null };
export type ImageShowcaseData = { note: string; rows: StillRow[]; models: StillModel[]; cells: StillCell[]; updatedAt: string | null };

export const EMPTY_IMAGE_SHOWCASE: ImageShowcaseData = { note: "", rows: [], models: [], cells: [], updatedAt: null };

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const str = (v: unknown): string => (typeof v === "string" ? v.trim() : "");
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
/** Only same-origin paths, https links, or (mock) data: images: never a javascript: URL in a src. */
const safeUrl = (v: unknown, allowData = false): string => {
  const s = str(v);
  if (/^\/(?!\/)/.test(s) || /^https:\/\//i.test(s)) return s;
  if (allowData && /^data:image\/(svg\+xml|png|jpeg|webp)[;,]/i.test(s)) return s;
  return "";
};

/** Reads the api's answer defensively: a bad row is dropped, never the page. */
export function parseImageShowcase(raw: unknown, opts: { allowDataUrls?: boolean } = {}): ImageShowcaseData {
  if (!isObj(raw)) return EMPTY_IMAGE_SHOWCASE;
  const rows = (Array.isArray(raw.rows) ? raw.rows : []).filter(isObj).map((r, i) => ({
    id: str(r.id), n: num(r.n) ?? i + 1, name: str(r.name), watch: str(r.watch), kind: (r.kind === "ref" ? "ref" : "style") as StillRow["kind"],
    tags: Array.isArray(r.tags) ? r.tags.filter((t): t is string => typeof t === "string") : [],
  })).filter((r) => r.id && r.name);
  const models = (Array.isArray(raw.models) ? raw.models : []).filter(isObj).map((m) => ({
    id: str(m.id), label: str(m.label), short: str(m.short) || str(m.label), provider: str(m.provider), credits: num(m.credits) ?? 0, refCredits: num(m.refCredits), refs: m.refs === true,
    rateSource: (m.rateSource === "unverified" ? "unverified" : "documented") as StillModel["rateSource"], isDefault: m.isDefault === true, madeSeconds: num(m.madeSeconds),
  })).filter((m) => m.id && m.label);
  const rowIds = new Set(rows.map((r) => r.id));
  const modelIds = new Set(models.map((m) => m.id));
  const cells = (Array.isArray(raw.cells) ? raw.cells : []).filter(isObj).map((c) => {
    const imageUrl = safeUrl(c.imageUrl, opts.allowDataUrls);
    return { rowId: str(c.rowId), modelId: str(c.modelId), imageUrl, thumbUrl: safeUrl(c.thumbUrl, opts.allowDataUrls) || imageUrl, seconds: num(c.seconds), madeAt: str(c.madeAt) || null };
  }).filter((c) => rowIds.has(c.rowId) && modelIds.has(c.modelId) && c.imageUrl);
  return { note: str(raw.note), rows, models, cells, updatedAt: str(raw.updatedAt) || null };
}

export const hasStills = (d: ImageShowcaseData | null | undefined): d is ImageShowcaseData => !!d && d.cells.length > 0;

export const stillOf = (d: ImageShowcaseData, rowId: string, modelId: string) => d.cells.find((c) => c.rowId === rowId && c.modelId === modelId) ?? null;

/**
 * The column a catalog id belongs to. The showcase runs a model on its cheaper route (Nano Banana Pro on Google, not
 * fal), so the other route's id finds it by the short name ("Nano Banana Pro on fal" -> "Nano Banana Pro").
 */
export function stillModelFor(d: ImageShowcaseData, id: string, label?: string): StillModel | null {
  const hit = d.models.find((m) => m.id === id);
  if (hit) return hit;
  const words = (s: string) => s.toLowerCase().replace(/\(fal\)|on fal|via fal/g, "").replace(/[^a-z0-9.]+/g, " ").trim();
  const want = label ? words(label) : "";
  return want ? (d.models.find((m) => words(m.label) === want || words(m.short) === want) ?? null) : null;
}

/** One model's stills, the asked row first (the relevant style), then page order. */
export function stillsFor(d: ImageShowcaseData, modelId: string, firstRow?: string | null): (StillCell & { row: StillRow })[] {
  const order = firstRow ? [...d.rows.filter((r) => r.id === firstRow), ...d.rows.filter((r) => r.id !== firstRow)] : d.rows;
  return order.flatMap((r) => {
    const c = stillOf(d, r.id, modelId);
    return c ? [{ ...c, row: r }] : [];
  });
}

/** The row to open on: the asked one when it has stills, else the first that has. */
export function pickRow(d: ImageShowcaseData, asked?: string | null): StillRow | null {
  return d.rows.find((r) => r.id === asked && d.cells.some((c) => c.rowId === r.id)) ?? d.rows.find((r) => d.cells.some((c) => c.rowId === r.id)) ?? null;
}

/**
 * The showcase row that fits a video: its look first (cartoon, anime, 3D, explainer), then its format, else its
 * scenes' tags (the same order as the Director's styleFor). Null: no project, show page order.
 */
export function rowForProject(p: { format?: string | null; brief?: unknown; script?: { scenes?: { tags?: string[] }[] } | null } | null | undefined): string | null {
  if (!p) return null;
  const look = `${p.format ?? ""} ${str((p.brief as { look?: unknown } | undefined)?.look)} ${str((p.brief as { lookId?: unknown } | undefined)?.lookId)}`.toLowerCase();
  if (/anime/.test(look)) return "anime";
  if (/3d|pixar|clay/.test(look)) return "animated-3d";
  if (/cartoon|storybook|flat2d|2d/.test(look)) return "cartoon-2d";
  if (/explainer|whiteboard/.test(look)) return "explainer";
  const byFormat: Record<string, string> = { documentary: "documentary", podcast: "talking-head", "music-video": "abstract", ad: "product" };
  if (p.format && byFormat[p.format]) return byFormat[p.format]!;
  return rowForTags((p.script?.scenes ?? []).flatMap((s) => s.tags ?? []));
}

/** core's styleForTags on the web side: the style row sharing the most tags with a scene. */
const ROW_TAGS: [string, string[]][] = [
  ["cinematic", ["close-up"]], ["product", ["product", "text"]], ["action", ["action"]], ["cartoon-2d", ["dialogue"]], ["animated-3d", ["close-up"]],
  ["anime", ["action", "close-up"]], ["documentary", ["b-roll"]], ["landscape", ["landscape"]], ["talking-head", ["dialogue", "close-up"]],
  ["food", ["product", "close-up"]], ["abstract", ["b-roll"]], ["explainer", ["text"]],
];
export function rowForTags(tags: readonly string[]): string | null {
  if (!tags.length) return null;
  const want = new Set(tags);
  let best: string | null = null;
  let score = 0;
  for (const [id, t] of ROW_TAGS) {
    const n = t.filter((x) => want.has(x)).length;
    if (n > score) [best, score] = [id, n];
  }
  return best;
}

export type StillCompare = { key: string; title: string; detail: string; imageUrl: string; thumbUrl: string };

/** 2-4 stills of one row side by side (the stage); unknown or unmade ones drop out. */
export function compareStills(d: ImageShowcaseData, rowId: string, modelIds: string[]): StillCompare[] {
  const out: StillCompare[] = [];
  for (const id of modelIds) {
    const m = stillModelFor(d, id) ?? d.models.find((x) => x.short.toLowerCase() === id.toLowerCase() || x.label.toLowerCase().includes(id.toLowerCase())) ?? null;
    const c = m && stillOf(d, rowId, m.id);
    if (!m || !c || out.some((x) => x.key === m.id)) continue;
    const row = d.rows.find((r) => r.id === rowId);
    out.push({ key: m.id, title: m.short, detail: creditsWords(stillCredits(m, row?.kind)), imageUrl: c.imageUrl, thumbUrl: c.thumbUrl });
    if (out.length === 4) break;
  }
  return out;
}

/** "15 credits a picture" */
export const creditsWords = (n: number) => `${n} credit${n === 1 ? "" : "s"} a picture`;

/** What one picture of this kind of row costs with the model: a reference row is drawn from references (PRICING-1). */
export const stillCredits = (m: Pick<StillModel, "credits" | "refCredits">, kind?: StillRow["kind"]): number => (kind === "ref" && m.refCredits != null ? m.refCredits : m.credits);

/** True when the model costs more with reference pictures (GPT Image, FLUX.2 Pro). */
export const refsCostMore = (m: Pick<StillModel, "credits" | "refCredits">): boolean => m.refCredits != null && m.refCredits !== m.credits;

/** "15 credits a picture, 25 with references" (or just "15 credits a picture"). */
export const priceWordsOf = (m: Pick<StillModel, "credits" | "refCredits">): string => `${creditsWords(m.credits)}${refsCostMore(m) ? `, ${m.refCredits} with references` : ""}`;

let cached: Promise<ImageShowcaseData | null> | null = null;

/** GET /api/model-showcase/images (or the mock's data under VITE_MOCK=1), once per page. Null when it can't be read. */
export function loadImageShowcase(fetcher: typeof fetch = fetch, timeoutMs = 6000): Promise<ImageShowcaseData | null> {
  if (import.meta.env?.VITE_MOCK === "1") return import("./mock/showcaseImages").then((m) => m.mockImageShowcase());
  cached ??= (async () => {
    const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
    const t = setTimeout(() => ctl?.abort(), timeoutMs);
    try {
      const r = await fetcher("/api/model-showcase/images", { credentials: "omit", headers: { accept: "application/json" }, signal: ctl?.signal });
      const d = r.ok ? parseImageShowcase(await r.json()) : null;
      if (!d) cached = null;
      return d;
    } catch {
      cached = null;
      return null;
    } finally {
      clearTimeout(t);
    }
  })();
  return cached;
}

/** Tests only: forget the cached answer. */
export const resetImageShowcaseCache = () => void (cached = null);
